import { GridFilterItem, GridLogicOperator } from '@mui/x-data-grid-pro';
import { v4 } from 'uuid';
import { InventoryAsset } from '../../../types';
import { coordKey } from '../../../utils/helpers';

/**
 * Builds GridFilterItems for a regular address pin click.
 *
 * A regular-address infobox can contain two categories of assets:
 *  - Assets whose primary `address` is at the pin location.
 *  - Z-location assets that were merged into the pin because their
 *    `locationZLatitude/Longitude` matches the pin coordinates. For
 *    these assets only `locationZText` represents the pin location;
 *    their `address` field points to a different location entirely.
 *
 * We separate the two sets so the filter uses the right field for
 * each asset and avoids including unrelated addresses in the query.
 */
export const buildAddressFilterItems = (assets: InventoryAsset[], pinLat: number, pinLng: number): GridFilterItem[] => {
  const pinKey = coordKey(pinLat, pinLng);
  const addressValues = new Set<string>();
  const zTextValues = new Set<string>();

  for (const a of assets) {
    const zKey =
      a.locationZLatitude != null && a.locationZLongitude != null
        ? coordKey(a.locationZLatitude, a.locationZLongitude)
        : null;

    if (zKey === pinKey && a.locationZText) {
      zTextValues.add(a.locationZText);
    } else if (a.address) {
      addressValues.add(a.address);
    }
  }

  const filters: GridFilterItem[] = [];
  if (addressValues.size > 0) {
    filters.push({ id: v4(), field: 'address', operator: 'isAnyOf', value: [...addressValues] });
  }
  if (zTextValues.size > 0) {
    filters.push({ id: v4(), field: 'locationZText', operator: 'isAnyOf', value: [...zTextValues] });
  }
  return filters;
};

export const buildZLocationFilterItems = (locationZTexts: string[]): GridFilterItem[] => {
  const unique = [...new Set(locationZTexts.filter(Boolean))];
  return [
    { id: v4(), field: 'locationZText', operator: 'isAnyOf', value: unique },
    { id: v4(), field: 'address', operator: 'isAnyOf', value: unique }
  ];
};

export const buildALocationFilterItems = (locationATexts: string[]): GridFilterItem[] => {
  const unique = [...new Set(locationATexts.filter(Boolean))];
  return [
    { id: v4(), field: 'locationAText', operator: 'isAnyOf', value: unique },
    { id: v4(), field: 'address', operator: 'isAnyOf', value: unique }
  ];
};

export { GridLogicOperator };
